import { useEffect, useState } from 'react';

import BlogSectionOne from 'components/landing/blog-section/templates/blog-section-template-01';
import BlogSectionTwo from 'components/landing/blog-section/templates/blog-section-template-02';
import { cleanDictForJson } from 'utils/secure-json';
import CTABannerContainer from 'components/home/cta-sticky-banner/cta-banner-container';
import CustomContent from 'components/landing/custom-content/custom-content';
import CustomHead from 'core/custom-head';
import Deductibles from 'components/landing/deductibles/deductibles';
import Discount from 'components/landing/discount/discount';
import dynamic from 'next/dynamic';
import FindBestPrice from 'components/landing/find-best-price/find-best-price-v2';
import Footer from 'components/footer/footer';
import getCarName from 'utils/get-car-name';
import getConstants from 'utils/query-data-parser/landing/get-constant';
import Header from 'components/home/header';
import { Header as HeaderSection } from 'components/header/header';
import { HYDRATION_STRATEGY } from 'core/constants';
import InformationLinks from 'components/landing/information-links/information-links';
import InsuranceCompanySelectorTable from 'components/landing/tables/insurance-selector/insurance-company-selector';
import InsuranceProfileSection from 'components/landing/company-information/company-information-section';
import Journey from 'components/landing/journey/journey';
import LandingContextProvider from 'components/landing/context/landing-context-provider';
import LinkBag from 'components/landing/link-bag/link-bag';
import ListSection from 'components/landing/list-section/list-section';
import ModifyLocationForm from 'components/landing/modify-location/modify-location-form';
import { PageInfoContextProvider } from 'context/PageInfoContext';
import PremiumPrice from 'components/landing/premium-price/premium-price';
import PropTypes from 'prop-types';
import RelatedNoteContainer from 'components/landing/related-note-list/related-note-container';
import SchemaArticle from 'components/seo/schema/schema-article';
import ScriptContainer from 'components/container/script-container';
import Scroller from 'components/home/scroller';
import SeoTracking from 'components/seo/tracking/seo-tracking';
import TableLinkComponent from 'components/landing/index/table-link-component';
import TableResume from 'components/landing/index/table-resume';
import Testimonial from 'components/home/testimonial/testimonial';
import TopRatedInsurance from 'components/landing/top-rated-insurance/top-rated-insurance';
import { useScroll } from 'hooks';
import withHydrationOnDemand from 'react-hydration-on-demand';

const Top5 = dynamic(() => import('components/landing/top-5/top-5'));
const HorizontalTable = dynamic(() => import('components/landing/tables/horizontal-table/horizontal-table'));
const QuotationListSelectedSlider = dynamic(() => import('components/landing/pillar-insurance-company/quotations-list-selected-slider'));
const QuotationByCarCategorySection = dynamic(() => import('components/landing/pillar-insurance-company/quotation-by-category-section'));
const QuotationAvailableByCarsTable = dynamic(() => import('components/landing/tables/quotation-available-by-car/quotation-available-by-car'));

const JourneyH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(Journey);
const DiscountH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(Discount);
const InsuranceCompanySelectorTableH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(InsuranceCompanySelectorTable);
const DeductiblesH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(Deductibles);
const InsuranceProfileSectionH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(InsuranceProfileSection);
const PremiumPriceH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(PremiumPrice);
const TopRatedInsuranceH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(TopRatedInsurance);
const InformationLinksH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(InformationLinks);
const ModifyLocationFormH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(ModifyLocationForm);
const TestimonialH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(Testimonial);
const ListSectionH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(ListSection);
const FooterH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(Footer);
const CustomContentH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(CustomContent);
const RelatedNoteContainerH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(RelatedNoteContainer);
const BlogSectionOneH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(BlogSectionOne);
const BlogSectionTwoH = withHydrationOnDemand({ on: HYDRATION_STRATEGY })(BlogSectionTwo);
const LinkBagH = withHydrationOnDemand({ on: ['visible'] })(LinkBag);
const FindBestPriceH = withHydrationOnDemand({ on: ['visible'] })(FindBestPrice);

const Landing = ({ culture, payload, carName }) => {
    const [loadTotalPage, setLoadTotalPage] = useState(false);

    useEffect(() => {
        setTimeout(() => {
            setLoadTotalPage(true);
        }, 3000);
    }, []);

    useScroll((scroll) => {
        if (scroll > 0 && !loadTotalPage) {
            setLoadTotalPage(true);
        }
    }, [loadTotalPage]);

    return (
        <ScriptContainer breadcrumbList={payload.page_context.page_breadcrumb_list}>
            <SeoTracking
                templateName={payload.page_context.page_config.utm_campaign || 'contentap'}
                pageCode={payload.page_context.code}
            />
            <SchemaArticle pageInfo={payload} />
            <CustomHead pageInfo={payload} />
            <PageInfoContextProvider data={payload}>
                <LandingContextProvider>
                    <CTABannerContainer hideMobile />
                    <Scroller />
                    <Header />
                    <HeaderSection />
                    <FindBestPriceH showBreadcrumb />
                    <TableResume>
                        <TableLinkComponent id="quotation-by-cars-section" titleList={[1]}>
                            {loadTotalPage && (
                                <QuotationAvailableByCarsTable
                                    culture={culture}
                                    companyCode={payload.page_context.page_config.company_code || ''}
                                    familyCodeList={payload.page_context.page_dinamic_data.family_codes}
                                    carBrand={payload.page_context.page_config.car_brand}
                                    pageCode={payload.page_context.code}
                                />)
                            }
                        </TableLinkComponent>
                        {loadTotalPage && (
                            <TableLinkComponent id="quotation-by-car-category-section" titleList={[2]}>
                                <QuotationByCarCategorySection
                                    pageCode={payload.page_context.code}
                                    culture={culture}
                                    companyCode={payload.page_context.page_config.company_code || ''}
                                />
                            </TableLinkComponent>)
                        }
                        {loadTotalPage && (
                            <QuotationListSelectedSlider
                                pageCode={payload.page_context.code}
                                discount={payload.page_context.page_config.env.discount}
                            />)
                        }
                        {loadTotalPage && (
                            <TableLinkComponent id="top-5-section" titleList={[3]}>
                                <Top5
                                    pageCode={payload.page_context.code}
                                    discount={payload.page_context.page_config.env.discount}
                                />
                            </TableLinkComponent>)
                        }
                        <TableLinkComponent id="journey-section" titleList={[4]}>
                            <JourneyH />
                        </TableLinkComponent>
                        {loadTotalPage && (
                            <HorizontalTable
                                pageCode={payload.page_context.code}
                                culture={culture}
                                discount={payload.page_context.page_config.env.discount}
                                carName={carName}
                            />
                        )}
                        <TableLinkComponent id="discount-section" titleList={[5]}>
                            <DiscountH data={payload.page_context.page_dinamic_data.discount_list} />
                        </TableLinkComponent>
                        <InsuranceCompanySelectorTableH
                            data={payload.page_context.page_dinamic_data.related_insurers}
                            culture={culture}
                        />
                        <TableLinkComponent id="deductibles-section" titleList={[6]}>
                            <DeductiblesH />
                        </TableLinkComponent>
                        <TableLinkComponent id="insurance-profile-section" titleList={[7]}>
                            <InsuranceProfileSectionH />
                        </TableLinkComponent>
                        <CustomContentH content={payload.page_context.content} />
                        <RelatedNoteContainerH pageCode={payload.page_context.code} />
                        <TableLinkComponent id="premium-price-section" titleList={[8]}>
                            <PremiumPriceH data={payload.page_context.page_dinamic_data.car_quotation_range} />
                        </TableLinkComponent>
                        <TableLinkComponent id="top-rated-insurance-section" titleList={[9]}>
                            <TopRatedInsuranceH
                                pageCode={payload.page_context.code}
                                template={payload.template_type}
                                companyRating={payload.page_context.page_dinamic_data.insurance_company_rating}
                            />
                        </TableLinkComponent>
                        <TableLinkComponent id="information-links-section" titleList={[10]}>
                            <InformationLinksH data={payload.page_context.page_dinamic_data.link_list} />
                        </TableLinkComponent>
                        <ModifyLocationFormH />
                        <TableLinkComponent id="blog-one-section" titleList={[11]}>
                            <BlogSectionOneH />
                        </TableLinkComponent>
                        <TableLinkComponent id="blog-two-section" titleList={[12]}>
                            <BlogSectionTwoH />
                        </TableLinkComponent>
                    </TableResume>
                    <ListSectionH
                        relatedPageList={payload.page_context.random_car_page_list}
                        sectionTitle="insurance-car-landing-car"
                        withoutParent
                    />
                    <TestimonialH />
                    <ListSectionH
                        relatedPageList={payload.page_context.related_pages}
                        parentPage={payload.page_context.parent_page}
                        sectionTitle={payload.template_type}
                    />
                    <LinkBagH />
                    <FooterH />
                </LandingContextProvider>
            </PageInfoContextProvider>
        </ScriptContainer>
    );
};

export async function getServerSideProps ({ req }) {
    const data = req.apiData.data;
    const culture = data.page_context.page_lang;

    const payload = {
        ...data,
        ...getConstants(data.page_context)
    };

    const carName = getCarName(
        payload.page_context.deep_level,
        payload,
        payload.template_type
    );

    return {
        props: {
            messages: (await import(`../messages/insurance-company/${culture}.json`))
                .default,
            culture,
            locale: culture.split('-')[1],
            timeZone: req.apiData.timeZone,
            carName,
            payload: cleanDictForJson(payload)
        }
    };
};

Landing.propTypes = {
    culture: PropTypes.string,
    payload: PropTypes.object,
    carName: PropTypes.string
};

export default Landing;
